import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useServiceCategories } from 'hooks/api/useCommercial';
import { serviceCategoryService, coaLedgerService } from 'api/services';
import type { ServiceCategoryDto, ServiceCategoryQueryParams, CreateServiceCategoryRequest } from 'api/types';

interface FormData {
  categoryCode: string; categoryName: string; parentCategoryId: string;
  incomeLedgerId: string; description: string; statusCode: string;
}
const EMPTY: FormData = {
  categoryCode: '', categoryName: '', parentCategoryId: '', incomeLedgerId: '',
  description: '', statusCode: 'ACTIVE',
};
const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

// ============================================================================
// FORM MODAL
// ============================================================================

interface CategoryFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: ServiceCategoryDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const snapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const labelCache = useRef<Map<string, string>>(new Map());
  const ledgerCache = useRef<Map<string, string>>(new Map());

  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const formRef = useRef(form); formRef.current = form;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    const src = (mode === 'edit' || mode === 'copy') ? record : null;
    const d: FormData = src
      ? {
          // Copy mode: blank code (user must supply a new one)
          categoryCode: mode === 'copy' ? '' : src.categoryCode,
          categoryName: src.categoryName,
          parentCategoryId: src.parentCategoryId || '',
          incomeLedgerId: src.incomeLedgerId || '',
          description: src.description || '',
          statusCode: src.statusCode ?? 'ACTIVE',
        }
      : { ...EMPTY };
    if (src?.parentCategoryId && src.parentCategoryName) {
      labelCache.current.set(src.parentCategoryId, src.parentCategoryName);
    }
    if (src?.incomeLedgerId && src.incomeLedgerName) {
      ledgerCache.current.set(src.incomeLedgerId, `${src.incomeLedgerCode} - ${src.incomeLedgerName}`);
    }
    setForm(d); snapshotRef.current = JSON.stringify(d); setErrors({});
  }, [isOpen]);

  const isDirty = useMemo(
    () => snapshotRef.current ? JSON.stringify(form) !== snapshotRef.current : false,
    [form],
  );
  const upd = (patch: Partial<FormData>) => setForm(p => ({ ...p, ...patch }));
  const clearErr = (f: string) => setErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });
  const handleClose = () => { if (isDirty) setDiscardOpen(true); else onClose(); };

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const currentRecord = recordRef.current;
    const currentInstanceId = createInstanceIdRef.current;
    const savedForm = { ...formRef.current };
    const savedSnapshot = snapshotRef.current;
    const dirty = savedSnapshot ? JSON.stringify(savedForm) !== savedSnapshot : false;

    let modalId: string;
    let stripTitle: string;
    if (currentMode === 'create') {
      const idx = currentInstanceId || '1';
      modalId = `${tabId}-form-modal-create-${idx}`;
      const label = savedForm.categoryCode.trim() || savedForm.categoryName.trim();
      stripTitle = label ? `Add - ${label} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const key = currentRecord?.categoryCode || currentInstanceId || '1';
      modalId = `${tabId}-form-modal-copy-${key}`;
      const label = savedForm.categoryCode.trim() || currentRecord?.categoryCode || '';
      stripTitle = label ? `Copy - ${label}` : 'Copy Category';
    } else {
      const key = currentRecord?.categoryCode || currentRecord?.id || '';
      modalId = `${tabId}-form-modal-edit-${key}`;
      stripTitle = `Edit - ${currentRecord?.categoryCode || currentRecord?.categoryName || 'Category'}`;
    }

    minimize({
      id: modalId, title: stripTitle, moduleName, tabId, isDirty: dirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm); snapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentInstanceId : undefined,
          currentMode !== 'create' ? currentRecord?.categoryCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  useEffect(() => {
    if (minimizeRef) minimizeRef.current = handleMinimize;
  }, [minimizeRef, handleMinimize]);

  const loadParents = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await serviceCategoryService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(c => labelCache.current.set(c.id, `${c.categoryCode} - ${c.categoryName}`));
      return res.data
        .filter(c => c.id !== record?.id)
        .map(c => ({ value: c.id, label: `${c.categoryCode} - ${c.categoryName}` }));
    } catch {
      return [];
    }
  }, [record?.id]);

  const loadIncomeLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({
        accountTypeCode: 'INCOME', pageSize: 50, searchTerm: input || undefined,
      });
      res.data.forEach(l => ledgerCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch {
      return [];
    }
  }, []);

  const handleSave = async () => {
    const e: Record<string, string> = {};
    if (!form.categoryCode.trim()) e.categoryCode = 'Code is required';
    if (!form.categoryName.trim()) e.categoryName = 'Name is required';
    if (Object.keys(e).length) { setErrors(e); return; }
    try {
      const excludeId = mode === 'edit' ? record?.id : undefined;
      const ok = await serviceCategoryService.isCodeAvailable(form.categoryCode.trim(), excludeId);
      if (!ok) { setErrors({ categoryCode: 'This code already exists.' }); return; }
    } catch { /* server-side enforce */ }
    setErrors({}); setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      const payload: CreateServiceCategoryRequest = {
        categoryCode: form.categoryCode.trim(),
        categoryName: form.categoryName.trim(),
        parentCategoryId: form.parentCategoryId || undefined,
        incomeLedgerId: form.incomeLedgerId || undefined,
        description: form.description.trim() || undefined,
        statusCode: form.statusCode,
      };
      if (mode === 'edit' && record) {
        await serviceCategoryService.update(record.id, { ...payload, rowVersion: record.rowVersion });
      } else {
        await serviceCategoryService.create(payload);
      }
      setSaving(false);
      const verb = mode === 'edit' ? 'updated' : 'created';
      notificationService.toast.success(`Category "${form.categoryName}" ${verb}.`);
      onSaved(); onClose();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not save.');
    }
  };

  if (!isOpen) return null;
  const errCls = (f: string) => `form-input${errors[f] ? ' form-field-error' : ''}`;
  const headerText = mode === 'edit' ? 'Edit Category' : mode === 'copy' ? 'Copy Category' : 'Add New Category';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{headerText}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>
        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="categoryCode">
              <label className="form-label">Code <span className="text-danger">*</span></label>
              <input className={errCls('categoryCode')} value={form.categoryCode} maxLength={30}
                readOnly={mode === 'edit'}
                onChange={e => { clearErr('categoryCode'); upd({ categoryCode: e.target.value.toUpperCase() }); }}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}} />
              {errors.categoryCode && <div className="form-error-message">{errors.categoryCode}</div>}
            </div>
            <div className="form-group" data-field="categoryName">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input className={errCls('categoryName')} value={form.categoryName} maxLength={150}
                onChange={e => { clearErr('categoryName'); upd({ categoryName: e.target.value }); }} />
              {errors.categoryName && <div className="form-error-message">{errors.categoryName}</div>}
            </div>
            <div className="form-group">
              <SyutSelect label="Parent Category" isAsync loadOptions={loadParents} defaultOptions cacheOptions={false}
                seedOptions={form.parentCategoryId
                  ? [{ value: form.parentCategoryId, label: labelCache.current.get(form.parentCategoryId) || '' }]
                  : undefined}
                menuPortalTarget={document.body} value={form.parentCategoryId}
                onChange={v => upd({ parentCategoryId: (v as string) || '' })}
                placeholder="None (top level)" isClearable />
            </div>
            <div className="form-group">
              <SyutSelect label="Default Income Account" isAsync loadOptions={loadIncomeLedgers}
                defaultOptions cacheOptions={false}
                seedOptions={form.incomeLedgerId
                  ? [{ value: form.incomeLedgerId, label: ledgerCache.current.get(form.incomeLedgerId) || '' }]
                  : undefined}
                menuPortalTarget={document.body} value={form.incomeLedgerId}
                onChange={v => upd({ incomeLedgerId: (v as string) || '' })}
                placeholder="Income ledger (fallback for services)" isClearable />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <SyutSelect label="Status" options={STATUS_OPTIONS} value={form.statusCode}
                onChange={v => upd({ statusCode: (v as string) || '' })}
                isClearable={false} menuPortalTarget={document.body} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows={2} value={form.description}
              onChange={e => upd({ description: e.target.value })} />
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <IconSystem.check size={16} /> {mode === 'edit' ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`${mode === 'edit' ? 'Save changes to' : 'Create'} category "${form.categoryName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'} cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={discardOpen} title="Discard Changes?"
        message="You have unsaved changes. Discard them?" confirmLabel="Discard"
        cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)} />
    </>
  );
};

// ============================================================================
// SCREEN
// ============================================================================

type ViewMode = 'list' | 'grid';

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'categoryCode', label: 'Code' },
  { id: 'categoryName', label: 'Name' },
  { id: 'parent', label: 'Parent' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface ServiceCategoryScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ServiceCategoryScreen: React.FC<ServiceCategoryScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('service-category');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [queryParams, setQueryParams] = useState<ServiceCategoryQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useServiceCategories(queryParams);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('service-category-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selected, setSelected] = useState<ServiceCategoryDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) {
      while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleEdit = useCallback((r: ServiceCategoryDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.categoryCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((r: ServiceCategoryDto) => {
    const modalId = tabId ? `${tabId}-form-modal-copy-${r.categoryCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal, formOpen]);

  const handleRestoreModal = useCallback(
    (m: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => {
      if (m === 'create' && instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(r => r.categoryCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(m); setFormOpen(true);
    },
    [data],
  );

  const handleDelete = useCallback(async (r: ServiceCategoryDto) => {
    const ok = await confirmDelete({ resourceLabel: r.categoryName });
    if (!ok) return;
    try {
      await serviceCategoryService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Category "${r.categoryName}" deleted.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete.');
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<ServiceCategoryDto>[] = useMemo(() => [
    { id: 'categoryCode', header: 'Code', accessorKey: 'categoryCode', size: 130 },
    { id: 'categoryName', header: 'Name', accessorKey: 'categoryName', size: 240 },
    {
      id: 'parent', header: 'Parent', accessorKey: 'parentCategoryName', size: 200,
      cell: ({ row }) => (row.original as ServiceCategoryDto).parentCategoryName || '—',
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => (
        <span className="badge">{(row.original as ServiceCategoryDto).statusName || 'Active'}</span>
      ),
    },
  ], []);

  const rowActions = useCallback((r: ServiceCategoryDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
    }
    return items;
  }, [can, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Category'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  return (
    <EnterpriseScreenShell
      title="Service Categories"
      description="Groups for services"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.folder size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount: includeInactive ? 1 : 0,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={e => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setIncludeInactive(false);
                setQueryParams(p => ({ page: 1, pageSize: p.pageSize }));
              }}>Clear</button>
              <button className="btn btn-primary" onClick={() => {
                setQueryParams(p => ({ ...p, includeInactive: includeInactive || undefined, page: 1 }));
                setFilterOpen(false);
              }}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            frozenLeftColumns={['select', 'categoryCode']} frozenRightColumns={['actions']}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No categories yet — click Add.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.categoryCode}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.categoryName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.parentCategoryName || 'Top-level'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <CategoryFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Service Categories'}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />
    </EnterpriseScreenShell>
  );
};

export default ServiceCategoryScreen;
